import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { Shield, Loader2 } from 'lucide-react';
import { Law } from './Entities/Law';
import ArticleContent from './Components/laws/ArticleContent';
import SEO from './Components/SEO';

function useQueryParams() {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
}

export default function LawDetail({ lawType = 'dpdp-rules' }) {
  const params = useQueryParams();
  const lawId = params.get('id') || lawType;
  const chapterParam = params.get('chapter');
  const articleParam = params.get('article');
  const scheduleParam = params.get('schedule');
  const partParam = params.get('part');

  const chapterNum = chapterParam ? Number(chapterParam) : undefined;
  const articleNum = articleParam ? Number(articleParam) : undefined;
  const scheduleNum = scheduleParam ? Number(scheduleParam) : undefined;

  const { data: laws = [], isLoading } = useQuery({
    queryKey: ['laws'],
    queryFn: () => Law.list()
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
      </div>
    );
  }

  const law = laws.find(l => String(l.id) === String(lawId));

  if (!law) {
    return (
      <div className="text-center py-20">
        <Shield className="w-16 h-16 mx-auto text-blue-300 mb-4" />
        <h3 className="text-xl font-medium text-black mb-2">Law not found</h3>
        <p className="text-blue-700">The requested law could not be found.</p>
      </div>
    );
  }

  const seoTitle = law.title
    ? `${law.title} - DPDP Guide`
    : `${lawType === 'dpdp' ? 'DPDP Act' : 'DPDP Rules'} - DPDP Guide`;
  const seoDescription = law.title
    ? `Explore ${law.title} — sections, chapters, and detailed provisions under India's data protection framework.`
    : `Detailed guide to India's ${lawType === 'dpdp' ? 'Digital Personal Data Protection Act' : 'DPDP Rules'}.`;

  return (
    <>
    <SEO
      title={seoTitle}
      description={seoDescription}
      keywords={`${law.title || 'DPDP'}, data protection India, privacy law, DPDP compliance`}
    />
    <div className="main-bg" style={{ minHeight: '100vh', paddingTop: '1rem' }}>
      <div className="article-page mx-2 sm:mx-4 md:mx-auto">
        <ArticleContent law={law} chapterNum={chapterNum} articleNum={articleNum} scheduleNum={scheduleNum} schedulePart={partParam} />
      </div>
    </div>
    </>
  );
}